/* WHERE SHE LEFT OFF — the Ask Brill greeting (her ask, 2026-10-20).
 *
 * Ask Brill used to open with the same fixed line for everyone: "I recommend
 * starting with My Vision". Her words: "that's kind of confusing depending on
 * where she's at. It would be better to say where she left off."
 *
 * Two answers, both COUNTED FROM WHAT IS STORED, never from Brill:
 *   last — the module she most recently wrote in (her latest stored message).
 *   next — the first section not yet locked, in module order, in the phase she
 *          has access to. A row in `outputs` IS a lock, so done means stored,
 *          the same rule the sidebar's ticks follow.
 * They usually agree. When she has jumped ahead they don't, and the greeting
 * names both.
 *
 * Not yet: Momentum work (a page in Sites, a post) is not in `messages`, so a
 * coach who was last in Sites is told about the Accelerator module she last
 * wrote in. Named here so nobody reads the greeting as covering it.
 */
import { ACCELERATOR_MODULES, IDEATION_MODULES, MODULE_META } from "../constants/modules";
import { getVisiblePrompts } from "./messages";

const labelOf = (id) => MODULE_META[id]?.label || id;
/* The phase a module sits in, by the name the app's menu uses. "Accelerator"
   stays (her call, 2026-10-20): "Business Brain" may become a tagline, and
   renaming the phase is a lot of code. */
const PHASE_LABELS = { ideation: "Ideation", accelerator: "Accelerator" };
const phaseOf = (id) => PHASE_LABELS[MODULE_META[id]?.section] || null;

/**
 * The screen a nav id names, as { id, label }, or null for Ask Brill itself or
 * an id nothing names. A Momentum tool by its nav row's label (`navItems`:
 * MomentumHub's BUCKETS items), anything else by MODULE_META.
 */
export function placeOf(id, navItems = []) {
  if (!id || id === "chat") return null;
  const tool = (navItems || []).find((it) => (it.route || `m/${it.id}`) === id);
  const label = tool?.label || MODULE_META[id]?.label;
  return label ? { id, label } : null;
}

/* THE BUG (her report, 2026-10-22): "all of them are saying 'the last time you
   were working in the niche decoder' … that's not what I was working on." She
   works in Momentum, which is not in `messages`, so the newest message she had
   anywhere was an old Decoder one, on every account. Now `lastPlace` — the
   last screen she actually OPENED, kept by App — answers "where was she", and
   the message reading is only a fallback for someone without Momentum (whose
   work all IS in messages). With Momentum and no remembered place, say nothing
   rather than name the wrong one. And for her, "Pick up" means going back to
   that place — never starting an Accelerator section she was not in. */
export function whereLeftOff({ moduleMessages = {}, moduleOutputs = {}, hasAcceleratorAccess = false, hasMomentumAccess = false, lastPlace = null } = {}) {
  const candidates = [...IDEATION_MODULES, ...ACCELERATOR_MODULES];
  let last = null;
  let lastAt = "";
  candidates.forEach((id) => {
    (moduleMessages[id] || []).forEach((m) => {
      if (m?.created_at && m.created_at > lastAt) { lastAt = m.created_at; last = id; }
    });
  });

  const order = hasAcceleratorAccess ? ACCELERATOR_MODULES : IDEATION_MODULES;
  let next = null;
  for (const id of order) {
    const prompts = getVisiblePrompts(id, moduleMessages[id] || []);
    const stored = moduleOutputs[id] || {};
    const open = prompts.find((p) => !stored[p]);
    if (open) { next = { id, label: labelOf(id), section: open, phase: phaseOf(id) }; break; }
  }

  const fromMessages = last && !hasMomentumAccess ? { id: last, label: labelOf(last) } : null;
  const where = lastPlace?.id && lastPlace?.label ? lastPlace : fromMessages;
  return {
    last: where,
    /* The next open section is offered only where it IS picking up: no place
       known (an Accelerator client's first visit), or the place she was is
       the module that section lives in. */
    next: next && (!where ? !hasMomentumAccess : where.id === next.id) ? next : null,
  };
}
